import { useState, useCallback } from 'react';

const INITIAL_BALANCE = 10000;

interface UseBettingStateReturn {
  balance: number;
  currentBet: number | null;
  bettingEnabled: boolean;
  showBetOverlay: boolean;
  showCelebration: boolean;
  celebrationAmount: number;
  placeBet: (amount: number) => void;
  calculatePayout: (payMultiplier: number) => void;
  handleLoss: () => void;
  restoreBalance: (amount: number) => void;
  setBettingEnabled: (enabled: boolean) => void;
  setShowBetOverlay: (show: boolean) => void;
  setShowCelebration: (show: boolean) => void;
}

/**
 * Custom hook to manage betting state
 * 
 * Manages:
 * - Balance initialization and tracking
 * - Bet placement with balance deduction
 * - Payout calculation for wins
 * - Balance restoration on errors
 * - Betting enabled/disabled state
 * - UI overlay states
 */
export const useBettingState = (): UseBettingStateReturn => {
  const [balance, setBalance] = useState<number>(INITIAL_BALANCE);
  const [currentBet, setCurrentBet] = useState<number | null>(null);
  const [bettingEnabled, setBettingEnabled] = useState<boolean>(true);
  const [showBetOverlay, setShowBetOverlay] = useState<boolean>(false);
  const [showCelebration, setShowCelebration] = useState<boolean>(false);
  const [celebrationAmount, setCelebrationAmount] = useState<number>(0);

  /**
   * Place a bet by deducting the amount from balance
   * Requirements: 1.1, 2.2
   */
  const placeBet = useCallback((amount: number) => {
    console.log('[useBettingState] placeBet called with amount:', amount);
    setBalance((prevBalance) => {
      console.log('[useBettingState] Previous balance:', prevBalance, 'New balance:', prevBalance - amount);
      return prevBalance - amount;
    });
    setCurrentBet(amount);
    console.log('[useBettingState] Current bet set to:', amount);
  }, []);

  /**
   * Calculate payout for a win and add to balance
   * Formula: payout = betAmount × 2 × payMultiplier
   * Requirements: 2.3, 5.4
   */
  const calculatePayout = useCallback((payMultiplier: number) => {
    console.log('[useBettingState] calculatePayout called with multiplier:', payMultiplier);
    console.log('[useBettingState] currentBet:', currentBet);

    if (currentBet === null) {
      console.log('[useBettingState] currentBet is null, returning early');
      return;
    }

    const payout = currentBet * 2 * payMultiplier;
    console.log('[useBettingState] Calculated payout:', payout);
    console.log('[useBettingState] Setting balance, celebration amount, and showing celebration');

    setBalance((prevBalance) => {
      console.log('[useBettingState] Previous balance:', prevBalance, 'New balance:', prevBalance + payout);
      return prevBalance + payout;
    });
    setCelebrationAmount(payout);
    setShowCelebration(true);
    setCurrentBet(null);

    console.log('[useBettingState] calculatePayout completed');
  }, [currentBet]);

  /**
   * Handle a loss by clearing the current bet
   * Balance was already deducted when bet was placed
   * Requirements: 2.4, 5.5
   */
  const handleLoss = useCallback(() => {
    setCurrentBet(null);
  }, []);

  /**
   * Restore balance by adding back the specified amount
   * Used for error handling when bet placement fails
   * Requirements: 2.5, 6.5
   */
  const restoreBalance = useCallback((amount: number) => {
    setBalance((prevBalance) => prevBalance + amount);
    setCurrentBet(null);
  }, []);

  return {
    balance,
    currentBet,
    bettingEnabled,
    showBetOverlay,
    showCelebration,
    celebrationAmount,
    placeBet,
    calculatePayout,
    handleLoss,
    restoreBalance,
    setBettingEnabled,
    setShowBetOverlay,
    setShowCelebration,
  };
};
